/* global updateProcurementPlanItemUrl syncProcurementPlanItemExpensesUrl syncProcurementPlanItemExpenseablesUrl */
/* global deleteProcurementPlanItemUrl deleteProcurementPlanItemAttachmentUrl */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Modal } from 'flowbite';

import ProcurementEditModal from '../../procurement_plan/ProcurementEditModal';
import ProcurementModal from '../../procurement_plan/ProcurementModal';
import ProcurementPrModal from '../../procurement_plan/ProcurementPrModal';
import { useProcurement } from '../context/ProcurementContext';

import PlanGantt from './PlanGantt';
import RegisterTable from './RegisterTable';

import { modalOptions } from 'Helpers/modalOptions';
import { apiClient, isSetupGateError } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import SegmentedControl from 'CommonComponents/SegmentedControl';
import ToggleInput from 'CommonComponents/ToggleInput';

import { useCustomEvent } from 'Hooks/useGlobalDOMEvents';
import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';
import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import SearchIcon from 'Assets/icons/search.svg';
import SortIcon from 'Assets/icons/sort.svg';

// IB-702 — the department-scoped Plan has just two views: the register ("Plan") and the Gantt.
const DEPT_VIEWS = { REGISTER: 'register', GANTT: 'gantt' };

// Grouping is fixed to category for the department-scoped Plan (no Category|Department toggle).
// Hoisted to module scope so it's a stable reference in the fetch useCallback dependency arrays.
const GROUP_KEY = 'category';

/**
 * IB-702 — the department-scoped Procurement Plan tab. A trimmed clone of the central IB-700 PlanTab:
 * fixed to category grouping, only the register ("Plan") and Gantt views, and every fetch carries the
 * `department` (query scope) + `department_context_id` (resource flags) of the current department. The
 * register + Gantt gain explicit Description + Value columns. All endpoints and modal flows are reused
 * verbatim from the central Plan tab.
 */
const DeptPlanTab = () => {
    const { settings, routes } = useProcurement();
    const currencySymbol = settings.currencySymbol;
    const departmentContextId = settings.departmentContextId;
    const toast = useToast();

    // Toolbar state. Grouping is fixed to category; only the register / Gantt views exist.
    const [view, setView] = useState(DEPT_VIEWS.REGISTER);
    const [highPriority, setHighPriority] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    // Register data (grouped payloads keyed by category) + independent pagination per section.
    const [outstanding, setOutstanding] = useState({ data: [], loading: true });
    const [completed, setCompleted] = useState({ data: [], loading: true });
    const outstandingPagination = usePagination({ perPage: 25 });
    const completedPagination = usePagination({ perPage: 25 });

    // The Gantt shows the WHOLE pipeline, fetched independently of the two register pagers. `groups`
    // keeps the grouped payload (source of truth, consumed by the Gantt).
    const [boardItems, setBoardItems] = useState({ groups: [], loading: true });

    // Procurement-plan meta (policies, categories, statuses, priorities, complexities, plan).
    const [meta, setMeta] = useState(null);

    // Modal plumbing (reuses the existing Edit / View / PR modals + their fetches).
    const [currentProcurement, setCurrentProcurement] = useState(null);
    const [expensesTree, setExpensesTree] = useState([]);
    const [existedIds, setExistedIds] = useState([]);
    const [expenseParams, setExpenseParams] = useState({ sort_by: 'name', order: 'asc' });
    const [isCreatePrMode, setIsCreatePrMode] = useState(false);
    const [loadingProcurement, setLoadingProcurement] = useState(false);
    const [creating, setCreating] = useState(false);

    const editModal = useModal();
    const viewModal = useModal();
    const prModal = useModal();

    const deleteModalRef = useRef();
    const [deleteModalElement, setDeleteModalElement] = useState(null);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [deleteCompleted, setDeleteCompleted] = useState(false);
    const [deleteFailed, setDeleteFailed] = useState(false);
    const [deleteFailedMsg, setDeleteFailedMsg] = useState('');
    const [confirmationText, setConfirmationText] = useState('');

    const emitRefetchDashboard = useCustomEvent({ eventName: 'REFETCH_PROCUREMENT_DASHBOARD' });

    const resetRegisterPages = () => {
        outstandingPagination.setPage(1);
        completedPagination.setPage(1);
    };

    // Debounce the predictive search box (~300ms) so the register filters live as the user types.
    useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            resetRegisterPages();
        }, 300);
        return () => clearTimeout(handle);
    }, [searchInput]);

    // Every fetch carries both the `department` query scope AND the `department_context_id` used to
    // compute the per-department resource flags (dept_owned_expense_ids, is_multi_dept, is_other_dept).
    const commonParams = useMemo(
        () => ({
            group_by: GROUP_KEY,
            department: departmentContextId,
            department_context_id: departmentContextId,
            high_priority: highPriority ? 1 : 0,
            search: search || undefined,
        }),
        [departmentContextId, highPriority, search],
    );

    const applyMeta = (pagination, data) => {
        const meta = data?.meta ?? {};
        pagination.setTotal(meta.total ?? 0);
        pagination.setLastPage(meta.last_page ?? 1);
        pagination.setFrom(meta.from ?? 0);
        pagination.setTo(meta.to ?? 0);
        if (pagination.page > 1 && (data?.data ?? []).length === 0) {
            pagination.setPage(pagination.page - 1);
        }
    };

    const fetchOutstanding = useCallback(() => {
        setOutstanding((prev) => ({ ...prev, loading: true }));
        apiClient
            .get(routes.fetchProcurementPlanItemsUrl, {
                params: { ...commonParams, status: 'outstanding', per_page: outstandingPagination.perPage, page: outstandingPagination.page },
            })
            .then(({ data }) => {
                setOutstanding({ data: data.data ?? [], loading: false });
                applyMeta(outstandingPagination, data);
            })
            .catch(() => setOutstanding({ data: [], loading: false }));
    }, [routes.fetchProcurementPlanItemsUrl, commonParams, outstandingPagination.page, outstandingPagination.perPage]);

    const fetchCompleted = useCallback(() => {
        setCompleted((prev) => ({ ...prev, loading: true }));
        apiClient
            .get(routes.fetchProcurementPlanItemsUrl, {
                params: { ...commonParams, status: 'completed', per_page: completedPagination.perPage, page: completedPagination.page },
            })
            .then(({ data }) => {
                setCompleted({ data: data.data ?? [], loading: false });
                applyMeta(completedPagination, data);
            })
            .catch(() => setCompleted({ data: [], loading: false }));
    }, [routes.fetchProcurementPlanItemsUrl, commonParams, completedPagination.page, completedPagination.perPage]);

    // The Gantt needs the COMPLETE item set across all stages, independent of the register pagers. The
    // items.index endpoint caps per_page at 100 (validation: in:10,25,50,100), so page through until
    // every item is collected. Status is omitted so a single pass covers both outstanding and
    // completed; the High-priority / search filters still apply. Groups (keyed by category) are merged
    // across pages preserving order.
    const fetchBoardItems = useCallback(async () => {
        setBoardItems((prev) => ({ ...prev, loading: true }));
        try {
            const groupOrder = [];
            const groupsByHeading = {};
            let page = 1;
            let lastPage = 1;
            do {
                const { data } = await apiClient.get(routes.fetchProcurementPlanItemsUrl, {
                    params: { ...commonParams, per_page: 100, page },
                });
                (data.data ?? []).forEach((group) => {
                    const heading = group[GROUP_KEY];
                    if (!groupsByHeading[heading]) {
                        groupsByHeading[heading] = { ...group, items: [] };
                        groupOrder.push(heading);
                    }
                    groupsByHeading[heading].items.push(...(group.items ?? []));
                });
                lastPage = data.meta?.last_page ?? 1;
                page += 1;
            } while (page <= lastPage);

            const groups = groupOrder.map((heading) => groupsByHeading[heading]);
            setBoardItems({ groups, loading: false });
        } catch (error) {
            setBoardItems({ groups: [], loading: false });
        }
    }, [routes.fetchProcurementPlanItemsUrl, commonParams]);

    const isBoardView = view === DEPT_VIEWS.GANTT;

    const refetchRegister = useCallback(() => {
        fetchOutstanding();
        fetchCompleted();
        if (isBoardView) {
            fetchBoardItems();
        }
    }, [fetchOutstanding, fetchCompleted, fetchBoardItems, isBoardView]);

    const fetchMeta = useCallback(() => {
        apiClient
            .get(routes.fetchProcurementPlanUrl)
            .then(({ data }) => {
                setMeta(data);
            })
            .catch(() => {});
    }, [routes.fetchProcurementPlanUrl]);

    const fetchExpenses = useCallback(() => {
        apiClient
            .get(routes.fetchExpensesUrl, { params: expenseParams })
            .then(({ data }) => {
                setExpensesTree(data.expensesTree);
                setExistedIds(data.excludedExpensesIds);
            })
            .catch(() => {});
    }, [routes.fetchExpensesUrl, expenseParams]);

    const setOrdering = (e, sort) => {
        e.preventDefault();
        const order = sort === expenseParams.sort_by ? (expenseParams.order === 'asc' ? 'desc' : 'asc') : 'asc';
        setExpenseParams((prev) => ({ ...prev, sort_by: sort, order }));
    };

    const arrow = (name) => {
        if (name === expenseParams.sort_by) {
            return expenseParams.order === 'asc' ? <ChevronDownIcon className="tw-w-2 tw-h-2" /> : <ChevronUpIcon className="tw-w-2 tw-h-2" />;
        }
        return <SortIcon className="tw-w-3 tw-h-3" />;
    };

    const fetchProcurement = useCallback(
        (id) => {
            if (!id) {
                return;
            }
            setLoadingProcurement(true);
            apiClient
                .get(routes.fetchProcurementPlanItemUrl.replace('ITEM_ID', id))
                .then(({ data }) => setCurrentProcurement(data))
                .catch(() => setCurrentProcurement(null))
                .finally(() => setLoadingProcurement(false));
        },
        [routes.fetchProcurementPlanItemUrl],
    );

    useEffect(() => {
        fetchMeta();
        setDeleteModalElement(new Modal(deleteModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    useEffect(() => {
        fetchOutstanding();
    }, [fetchOutstanding]);

    useEffect(() => {
        fetchCompleted();
    }, [fetchCompleted]);

    useEffect(() => {
        if (isBoardView) {
            fetchBoardItems();
        }
    }, [isBoardView, fetchBoardItems]);

    useCustomEvent({ eventName: 'REFETCH_PROCUREMENT_DASHBOARD', onSignal: () => refetchRegister() });

    // Flatten the expenses tree into the "id -> false" selection map the Edit/PR modals expect.
    const expensesIds = useMemo(() => {
        if (!expensesTree || !expensesTree.length) {
            return {};
        }
        const selected = {};
        const walkParents = (parents) => {
            parents.forEach((parent) => {
                (parent.expenses || []).forEach((expense) => {
                    selected[expense.id] = false;
                });
                if (parent.type === 'template') {
                    const dividers = _.isArray(parent.dividers) ? parent.dividers : Object.values(parent.dividers);
                    walkParents(dividers);
                }
            });
        };
        const walk = (node) => {
            (node.expenses || []).forEach((expense) => {
                selected[expense.id] = false;
            });
            walkParents(_.isArray(node.packs) ? node.packs : Object.values(node.packs));
            walkParents(_.isArray(node.templates) ? node.templates : Object.values(node.templates));
        };
        expensesTree.forEach((department) => {
            Object.values(department.costcenters).forEach((cc) => {
                walk(cc);
                (_.isArray(cc.projects) ? cc.projects : Object.values(cc.projects)).forEach((project) => walk(project));
            });
        });
        return selected;
    }, [expensesTree]);

    // ── Handlers (mirror the legacy ProcurementPlanIndex flows) ──────────────────────────────
    const showEdit = (item) => {
        fetchProcurement(item?.id);
        setIsCreatePrMode(false);
        editModal.show();
    };

    const showView = (item) => {
        fetchProcurement(item?.id);
        viewModal.show();
    };

    const showPr = (item) => {
        fetchProcurement(item?.id);
        setIsCreatePrMode(true);
        prModal.show();
    };

    const createEa = (item) => {
        window.location.href = item.purchase_requisition_id
            ? routes.createPurchaseOrderUrl.replace('REQUISITION_ID', item.purchase_requisition_id)
            : routes.directPurchaseOrderUrl.replace('PROCUREMENT_PLAN_ID', item.plan_id).replace('ITEM_ID', item.id);
    };

    const createDc = (item) => {
        window.location.href = routes.directCommitmentUrl.replace('PROCUREMENT_PLAN_ID', item.plan_id).replace('ITEM_ID', item.id);
    };

    const confirmDelete = (item) => {
        setCurrentProcurement(item);
        setConfirmationText(
            item.completed ? $t('This is a completed procurement - are you sure you want to delete it?') : $t('Are you sure you want delete this procurement?'),
        );
        deleteModalElement.show();
    };

    const triggerDelete = () => {
        setDeleteInProgress(true);
        apiClient
            .delete(deleteProcurementPlanItemUrl.replace('ITEM_ID', currentProcurement.id))
            .then(() => {
                refetchRegister();
                fetchExpenses();
                setDeleteCompleted(true);
                setTimeout(() => emitRefetchDashboard(), 5000);
            })
            .catch((error) => {
                setDeleteFailedMsg(error?.response?.data?.message || $t('Could not be deleted'));
                setDeleteFailed(true);
            });
    };

    const addProcurement = () => {
        if (!meta?.plan?.id) {
            return;
        }
        setCreating(true);
        apiClient
            .post(routes.storeProcurementPlanItemUrl.replace('PROCUREMENT_PLAN_ID', meta.plan.id))
            .then(({ data }) => {
                refetchRegister();
                showEdit(data);
            })
            .catch((error) => {
                if (!isSetupGateError(error)) {
                    toast.error($t('Error creating Procurement'));
                }
            })
            .finally(() => setCreating(false));
    };

    const onModalSuccess = () => {
        refetchRegister();
        fetchExpenses();
    };

    const closeEditPr = () => {
        setIsCreatePrMode(false);
        setCurrentProcurement(null);
        editModal.hide();
        prModal.hide();
        viewModal.hide();
    };

    const modalSharedProps = {
        procurement: currentProcurement,
        policies: meta?.policies ?? [],
        categories: meta?.categories ?? [],
        priorities: meta?.priorities ?? [],
        complexities: meta?.complexities ?? [],
        statuses: meta?.statuses ?? [],
        expensesTree,
        expensesIds,
        existedIds,
        setOrdering,
        arrow,
        reSyncing: false,
        lockedExpenseIds: undefined,
        onCreated: onModalSuccess,
        saveUrl: typeof updateProcurementPlanItemUrl !== 'undefined' ? updateProcurementPlanItemUrl : undefined,
        storePRUrl: routes.storePRUrl,
        indexPRUrl: routes.indexPRUrl,
        syncExpensesUrl: typeof syncProcurementPlanItemExpensesUrl !== 'undefined' ? syncProcurementPlanItemExpensesUrl : undefined,
        syncExpenseablesUrl: typeof syncProcurementPlanItemExpenseablesUrl !== 'undefined' ? syncProcurementPlanItemExpenseablesUrl : undefined,
        downloadAttachmentUrl: routes.downloadProcurementPlanItemAttachmentUrl,
        deleteAttachmentUrl: typeof deleteProcurementPlanItemAttachmentUrl !== 'undefined' ? deleteProcurementPlanItemAttachmentUrl : undefined,
        fetched: !!currentProcurement && !loadingProcurement,
        loading: loadingProcurement,
        isCreatePrMode,
        setLoading: () => {},
        eventStartDate: settings.eventStartDate,
        eventEndDate: settings.eventEndDate,
        onClose: closeEditPr,
        onCancel: closeEditPr,
        onSuccess: onModalSuccess,
        onAttachmentDeleted: (id) => fetchProcurement(id),
        onExpensesSynced: (id) => {
            fetchProcurement(id);
            fetchExpenses();
            refetchRegister();
        },
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {/* One toolbar row: the view switcher on the left, every control on the right. The
                High priority toggle used to sit alone up here with Add and the search field on a
                second row below. `flex-wrap` on both this row and the right-hand group is what
                lets a narrow viewport stack them instead of clipping the search field. */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                {/* The shared control's `default` variant is the app's own `in-nav horizontal` /
                    `in-nav__item` pattern, so Plan | Gantt matches the Budget sub-tabs by
                    construction rather than by approximating them in Tailwind, as the local
                    switcher this replaces did. Deliberately not this folder's own
                    ./SegmentedControl — that one draws the same grey track with a white active
                    pill, which is the inconsistency being corrected. */}
                <SegmentedControl
                    variant="default"
                    options={[
                        { value: DEPT_VIEWS.REGISTER, label: $t('Plan') },
                        { value: DEPT_VIEWS.GANTT, label: $t('Gantt') },
                    ]}
                    value={view}
                    onChange={setView}
                />
                {/* Add and search keep the order they already had — the toggle joined their row,
                    it did not split them. High priority lands last in this group, after search
                    and furthest from the view switcher, so the two controls that filter what the
                    register shows (search, High priority) stay adjacent to each other while Add,
                    the primary action, stays first — closest to the switcher, not wedged between
                    the filtering controls. */}
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                    <Button variant="primary" small onClick={addProcurement} loading={creating} disabled={!meta?.plan?.id}>
                        {$t('+ Add procurement')}
                    </Button>
                    <div className="tw-relative tw-w-64">
                        <SearchIcon className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-my-auto tw-ms-3 tw-h-3.5 tw-w-3.5 tw-text-charcoal-50" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder={$t('Search procurements…')}
                            className="tw-w-full tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-py-2 tw-ps-9 tw-pe-3 tw-text-sm tw-text-charcoal placeholder:tw-text-charcoal-50 focus:tw-border-blue focus:tw-outline-none"
                        />
                    </div>
                    <ToggleInput
                        noBottomSpacing
                        label={$t('High priority')}
                        checked={highPriority}
                        onChange={(e) => {
                            setHighPriority(e.target.checked);
                            resetRegisterPages();
                        }}
                    />
                </div>
            </div>

            {/* Views */}
            {view === DEPT_VIEWS.REGISTER && (
                <div className="tw-flex tw-flex-col tw-gap-5">
                    <RegisterTable
                        title={$t('Outstanding procurements')}
                        groups={outstanding.data}
                        groupKey={GROUP_KEY}
                        loading={outstanding.loading}
                        pagination={outstandingPagination}
                        emptyText={$t('No outstanding procurements in this department.')}
                        currencySymbol={currencySymbol}
                        settings={settings}
                        completed={false}
                        showDescriptionValue
                        onEdit={showEdit}
                        onView={showView}
                        onDelete={confirmDelete}
                        onCreatePR={showPr}
                        onCreateEA={createEa}
                        onCreateDC={createDc}
                    />
                    <RegisterTable
                        title={$t('Completed procurements')}
                        groups={completed.data}
                        groupKey={GROUP_KEY}
                        loading={completed.loading}
                        pagination={completedPagination}
                        emptyText={$t('No completed procurements in this department.')}
                        currencySymbol={currencySymbol}
                        settings={settings}
                        completed
                        showDescriptionValue
                        onEdit={showEdit}
                        onView={showView}
                        onDelete={confirmDelete}
                        onCreatePR={showPr}
                        onCreateEA={createEa}
                        onCreateDC={createDc}
                    />
                </div>
            )}

            {view === DEPT_VIEWS.GANTT && (
                <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-p-5">
                    {boardItems.loading ? <LoadingSpinner /> : <PlanGantt groups={boardItems.groups} currencySymbol={currencySymbol} showDescription />}
                </div>
            )}

            {/* Reused modals */}
            <ProcurementEditModal
                id="dept-face-edit-procurement-modal"
                modal={editModal}
                {...modalSharedProps}
                onEdit={() => setIsCreatePrMode(false)}
                onCancelEdit={() => setIsCreatePrMode(true)}
                onRefresh={(id) => fetchProcurement(id)}
            />
            <ProcurementPrModal
                id="dept-face-procurement-pr-modal"
                modal={prModal}
                {...modalSharedProps}
                onEdit={() => setIsCreatePrMode(false)}
                onCancelEdit={() => setIsCreatePrMode(true)}
            />
            <ProcurementModal
                id="dept-face-procurement-modal"
                modal={viewModal}
                procurement={currentProcurement}
                fetched={!!currentProcurement && !loadingProcurement}
                loading={loadingProcurement}
                onClose={closeEditPr}
                downloadAttachmentUrl={routes.downloadProcurementPlanItemAttachmentUrl}
            />

            <ConfirmationModal
                closeHandler={() => {}}
                successHandler={() => deleteModalElement && deleteModalElement.hide()}
                triggerAction={triggerDelete}
                cleanStates={() => {
                    setDeleteInProgress(false);
                    setDeleteFailed(false);
                    setDeleteCompleted(false);
                    setDeleteFailedMsg('');
                }}
                allowAction={true}
                modalId="dept-face-delete-procurement"
                modalRef={deleteModalRef}
                modalElement={deleteModalElement}
                request={deleteInProgress}
                actionCompleted={deleteCompleted}
                actionFailed={deleteFailed}
                confirmationMsg={confirmationText}
                actionCompletedMsg={$t('Deleted')}
                actionFailedMsg={deleteFailedMsg || $t('Could not be deleted')}
                title={$t('Delete procurement')}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Delete')}
            />
        </div>
    );
};

export default DeptPlanTab;
